// src/pages/Stories.js
import React, { useState, useEffect, useMemo } from 'react';
import processSentenceJisho from '../utils/processSentenceJisho';
import ClickableSentence from '../components/ClickableSentence';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import userActivityService from '../services/userActivityService';
import { useFirebase } from '../contexts/FirebaseContext';
import { Link } from 'react-router-dom';
import Pricing from './Pricing';
import '../styles/Stories.css'; // Import the CSS for Stories component

const DAILY_STORY_KEY = 'dailyStory';

const getStoryResetDate = () => {
    const now = new Date();
    const thresholdHour = 4;
    const thresholdMinute = 0;
    if (
        now.getHours() < thresholdHour ||
        (now.getHours() === thresholdHour && now.getMinutes() < thresholdMinute)
    ) {
        now.setDate(now.getDate() - 1);
    }
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const Stories = () => {
    const { userPlan, planExpiresAt } = useFirebase();
    const [settings, setSettings] = useState({
        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0
    });
    const [storyData, setStoryData] = useState({ title: "", titleTranslation: "", story: [], translation: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('original'); // Use 'original' or 'translation'
    const [loadingMessage, setLoadingMessage] = useState('Loading Story...');
    const [reloadCount, setReloadCount] = useState(0);
    const [maxReloadReached, setMaxReloadReached] = useState(false);
    const [maxReloadLimit, setMaxReloadLimit] = useState(1); // Default to trial limit
    const [showDebugInfo, setShowDebugInfo] = useState('disabled'); // Add debug info state
    const [showPricingOverlay, setShowPricingOverlay] = useState(false);

    const loadingMessages = useMemo(() => [
        "Loading Story...",
        "Just one moment...",
        "Thanks for your patience..."
    ], []);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                logger.debug('Stories - Fetching settings...');
                // Use apiClient to handle token refresh automatically
                const response = await apiClient.get('/api/settings');

                if (!response.ok) {
                    throw new Error(`Failed to fetch settings: ${response.status}`);
                }

                const data = await response.json();
                logger.debug('Stories - Settings fetched successfully:', data);

                setSettings({
                    learningLanguage: data.learningLanguage || "Spanish",
                    languageLevel: data.languageLevel || "Beginner",
                    dailyStoryRepeatNumber: data.dailyStoryRepeatNumber || 0
                });
            } catch (error) {
                logger.error("Stories - Error fetching settings:", error);

                // Fall back to localStorage if available
                const storedLanguage = localStorage.getItem('learningLanguage');
                if (storedLanguage) {
                    logger.debug('Stories - Using settings from localStorage as fallback');
                    setSettings({
                        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
                        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
                        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0
                    });
                }
            }
        };
        fetchSettings();
    }, []);

    const { learningLanguage, languageLevel, dailyStoryRepeatNumber } = settings;

    // Get max reload limit based on user plan
    const getStoryReloadLimit = () => {
        switch(userPlan) {
            case 'premium':
                return 10;
            case 'pro':
                return 5;
            case 'trial':
            default:
                return 1;
        }
    };

    // Update maxReloadLimit when userPlan changes
    useEffect(() => {
        setMaxReloadLimit(getStoryReloadLimit());
    }, [userPlan]);

    // Fetch reload count from server
    useEffect(() => {
        if (!learningLanguage) return;
        
        const fetchReloadCount = async () => {
            try {
                const reloadCounts = await userActivityService.getReloadCounts();
                const currentCount = reloadCounts.stories[learningLanguage] || 0;
                setReloadCount(currentCount);
                setMaxReloadReached(currentCount >= getStoryReloadLimit());
            } catch (error) {
                logger.error("Stories - Error fetching reload count:", error);
                setReloadCount(0);
            }
        };
        
        fetchReloadCount();
    }, [learningLanguage, userPlan]);

    useEffect(() => {
        let timer;
        if (loading) {
            let idx = 0;
            setLoadingMessage(loadingMessages[idx]);
            timer = setInterval(() => {
                idx = (idx + 1) % loadingMessages.length;
                setLoadingMessage(loadingMessages[idx]);
            }, 10000);
        } else {
            setLoadingMessage("");
        }
        return () => clearInterval(timer);
    }, [loading, loadingMessages]);

    // Fetch today's story from server
    useEffect(() => {
        if (!learningLanguage) return;
        
        const fetchStoryFromServer = async () => {
            try {
                const userActivity = await userActivityService.getUserActivity();
                const storedStory = userActivity.stories?.[learningLanguage];
                
                if (storedStory) {
                    logger.debug('Stories - Using cached story from server');
                    setStoryData(storedStory);
                } else {
                    setStoryData({ title: "", titleTranslation: "", story: [], translation: [] });
                }
            } catch (error) {
                logger.error("Stories - Error fetching story from server:", error);
                setStoryData({ title: "", titleTranslation: "", story: [], translation: [] });
            }
        };
        
        fetchStoryFromServer();
    }, [languageLevel, learningLanguage]);

    const fetchStory = async () => {
        setLoading(true);
        setLoadingMessage("Loading your story...");
        setError(null);

        try {
            // Get Chinese character type from localStorage if learning Chinese
            let chineseCharacterType = null;
            if (learningLanguage === 'Chinese') {
                chineseCharacterType = localStorage.getItem('chineseCharacterType') || 'Simplified';
                logger.debug('Stories - Using Chinese character type:', chineseCharacterType);
            }

            logger.debug('Stories - Fetching story with settings:', {
                languageLevel,
                learningLanguage,
                dailyStoryRepeatNumber,
                chineseCharacterType: learningLanguage === 'Chinese' ? chineseCharacterType : 'N/A'
            });

            // Use apiClient to handle token refresh
            const data = await apiClient.post('/api/generateStory', {
                languageLevel,
                learningLanguage,
                dailyStoryRepeatNumber: Number(dailyStoryRepeatNumber),
                ...(learningLanguage === 'Chinese' && { chineseCharacterType })
            });
            
            // Log the parsed data
            logger.debug('Stories - Received story data:', data);

            if (data.error) {
                logger.error('Stories - Server returned error object:', data.error);
                throw new Error(data.error);
            }

            // Verify story data structure before proceeding
            if (!data.story || !Array.isArray(data.story) || data.story.length === 0) {
                logger.error('Stories - Received invalid story data structure:', data);
                throw new Error('Received invalid story data from server');
            }

            // Store the story on server and get updated reload count
            logger.debug('Stories - About to call reloadStory with language:', learningLanguage);
            try {
                const result = await userActivityService.reloadStory(learningLanguage, data);
                logger.debug('Stories - reloadStory result:', result);
                
                // Update local state
                setStoryData(data);
                
                // Check if the result has reload information
                if (result && typeof result.reloadCount === 'number') {
                    setReloadCount(result.reloadCount);
                    
                    if (result.reloadCount >= getStoryReloadLimit()) {
                        setMaxReloadReached(true);
                    }
                }
            } catch (reloadError) {
                logger.error('Stories - Error in reloadStory:', reloadError);
                logger.error('Stories - reloadStory was called with data:', data);
                throw new Error(`Error saving story: ${reloadError.message}`);
            }
        } catch (err) {
            logger.error("Stories - Error fetching story:", err);
            setError("Could not load the daily story. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Check if trial has expired or subscription is canceled
    const isTrialExpired = () => {
        // Always consider canceled plans as expired
        if (userPlan === 'canceled') return true;
        
        // Check if trial is expired by date
        if (userPlan !== 'trial' || !planExpiresAt) return false;
        const now = new Date();
        const expiration = new Date(planExpiresAt);
        return now > expiration;
    };
    
    // Get appropriate message based on plan status
    const getAccessLimitMessage = () => {
        if (userPlan === 'canceled') {
            return (
                <>
                    Your subscription has been canceled. Please <Link 
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }} 
                        to="#" 
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to regain access.
                </>
            );
        } else if (userPlan === 'trial' && isTrialExpired()) {
            return (
                <>
                    Your trial has ended. Please <Link 
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }} 
                        to="#" 
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to continue using this feature.
                </>
            );
        } else {
            return userPlan === 'trial' ? (
                <>
                    You have reached your max {getStoryReloadLimit()} story limit for {learningLanguage}. You can <Link 
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }} 
                        to="#" 
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to a paid plan or come back tomorrow for more!
                </>
            ) : `You have reached your max ${getStoryReloadLimit()} story limit for ${learningLanguage}. Please come back tomorrow for more!`;
        }
    };

    // Handle closing the pricing overlay
    const handleClosePricing = () => {
        setShowPricingOverlay(false);
    };

    const handleLoadButtonClick = async () => {
        // If trial has expired, don't allow loading
        if (isTrialExpired()) {
            return;
        }
        
        if (maxReloadReached) return;
        await fetchStory();
    };

    const buttonLabel = (storyData.story.length > 0)
        ? "Press to reload a story..."
        : "Press to load a story...";

    return (
        <div className="stories-container">
            <div className="page-title">Daily {learningLanguage} Story</div>
            
            {loading ? (
                <div className="center-text">
                    <div className="loading-text">{loadingMessage}</div>
                </div>
            ) : error ? (
                <div className="center-text">
                    <div className="error-text" style={{ 
                        color: '#c41c1c', 
                        fontSize: '1rem', 
                        fontWeight: '500'
                    }}>{error}</div>
                </div>
            ) : storyData.story.length === 0 ? (
                <div className="center-text">
                    <div className="loading-stories">No story loaded.</div>
                </div>
            ) : (
                <>
                    {/* Tabs for switching between original and translation - moved to top */}
                    <div className="story-tabs">
                        <div 
                            className={`story-tab ${activeTab === 'original' ? 'active' : ''}`}
                            onClick={() => setActiveTab('original')}
                        >
                            {learningLanguage}
                        </div>
                        <div 
                            className={`story-tab ${activeTab === 'translation' ? 'active' : ''}`}
                            onClick={() => setActiveTab('translation')}
                        >
                            English
                        </div>
                    </div>
                
                    {/* Display title based on active tab */}
                    <div className="story-title-header">
                        {activeTab === 'original' ? (
                            <h3 className="story-title" dangerouslySetInnerHTML={{ __html: storyData.title }} />
                        ) : (
                            <h3 className="story-title">{storyData.titleTranslation}</h3>
                        )}
                    </div>
                    
                    <div className="story-wrapper">
                        {/* Content wrapper - shows either original or translation based on active tab */}
                        <div className={`story-content ${activeTab === 'original' ? 'active' : ''}`}>
                            <div className="story-box">
                                {storyData.story.map((sentence, index) => (
                                    <ClickableSentence
                                        key={index}
                                        sentence={processSentenceJisho(sentence)}
                                        lookupKey={`saved${learningLanguage}Words`}
                                        language={learningLanguage}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        <div className={`story-content ${activeTab === 'translation' ? 'active' : ''}`}>
                            <div className="story-box">
                                {Array.isArray(storyData.translation)
                                    ? storyData.translation.map((transSentence, index) => (
                                        <p key={index} className="translation-text">
                                            {processSentenceJisho(transSentence)}
                                        </p>
                                    ))
                                    : typeof storyData.translation === 'string'
                                        ? storyData.translation
                                            .split('\n')
                                            .filter(line => line.trim() !== "")
                                            .map((transSentence, index) => (
                                                <p key={index} className="translation-text">
                                                    {processSentenceJisho(transSentence)}
                                                </p>
                                            ))
                                        : null
                                }
                            </div>
                        </div>
                    </div>
                </>
            )}
            
            {/* Warning message outside of bottom bar */}
            {isTrialExpired() ? (
                <div className="max-reload-warning" style={{ 
                    marginTop: '15px', 
                    textAlign: 'center',
                    color: '#c41c1c',
                    fontSize: '1rem',
                    fontWeight: '500'
                }}>
                    <span>{getAccessLimitMessage()}</span>
                </div>
            ) : (
                maxReloadReached && (
                    <div className="max-reload-warning" style={{ 
                        marginTop: '15px', 
                        textAlign: 'center',
                        color: '#c41c1c',
                        fontSize: '1rem',
                        fontWeight: '500'
                    }}>
                        {getAccessLimitMessage()}
                    </div>
                )
            )}
            
            {/* Fixed bottom button */}
            <div className="bottom-bar">
                <div className="story-button-container">
                    <button
                        onClick={handleLoadButtonClick}
                        disabled={maxReloadReached || loading || isTrialExpired()}
                        className="reload-button"
                    >
                        {loading ? "Loading..." : buttonLabel}
                    </button>
                </div>
            </div>
            
            {/* Debug information box */}
            <div className="debug-box" style={{ 
                position: 'fixed', 
                top: '70px', 
                right: '10px', 
                backgroundColor: '#f0f0f0', 
                padding: '10px', 
                borderRadius: '5px',
                boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                fontSize: '12px',
                zIndex: 1000,
                display: showDebugInfo === 'enabled' ? 'block' : 'none'
            }}>
                <div><strong>Plan:</strong> {userPlan || 'Not available'}</div>
                <div><strong>Max Reloads:</strong> {maxReloadLimit}</div>
                <div><strong>Current Reloads:</strong> {reloadCount || 0}</div>
                <div><strong>Remaining:</strong> {maxReloadLimit - (reloadCount || 0)}</div>
                <button 
                    onClick={() => setShowDebugInfo('disabled')}
                    style={{
                        position: 'absolute',
                        top: '-15px',
                        right: '-15px',
                        backgroundColor: '#ff5555',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '10px',
                        cursor: 'pointer'
                    }}
                >
                    X
                </button>
            </div>
            
            {/* Debug toggle button - always visible */}
            <button 
                onClick={() => setShowDebugInfo('enabled')}
                style={{
                    position: 'fixed',
                    top: '70px',
                    right: '10px',
                    backgroundColor: '#555',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '5px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    zIndex: 999,
                    display: showDebugInfo === 'disabled' ? 'none' : 'block'
                }}
            >
                Debug
            </button>

            {/* Show pricing overlay if needed */}
            {showPricingOverlay && (
                <Pricing onClose={handleClosePricing} />
            )}
        </div>
    );
};

export default Stories;
